import { Link } from "wouter";

export const lastUpdatedLabel = "20 May 2026";

/**
 * ConnectID Minimum Customer Terms — FortifyID adaptation of the AP+
 * "ConnectID Representative and Beneficiary Minimum Customer Terms v1.0".
 *
 * FortifyID is the ConnectID Representative; the customer workspace that
 * accepts these terms is the Beneficiary. Bracketed placeholders from the
 * AP+ template have been completed for FortifyID's arrangement, and the
 * drafting-note paragraph applicable where the Representative makes the
 * ConnectID identity service available to the Beneficiary as part of its
 * own platform has been selected.
 *
 * IMMUTABLE SNAPSHOT — do not edit. Publish changes as a new dated version.
 */
export default function ConnectIdTermsV2026_05_20() {
  return (
    <>
      <p>
        These ConnectID Minimum Customer Terms (<strong>"ConnectID Terms"</strong>)
        govern your organisation's use of the ConnectID identity-verification
        service as it is made available to you through the FortifyID platform.
        They are required by Australian Payments Plus Limited (ACN 649 744 203)
        (<strong>"AP+"</strong>), the operator of ConnectID, and have the same
        legal effect as the AP+ "ConnectID Representative and Beneficiary
        Minimum Customer Terms". They are in addition to, and form part of, the
        FortifyID <Link href="/legal/terms">Terms of Service</Link> and{" "}
        <Link href="/legal/dpa">Data Processing Agreement</Link>. If there is a
        conflict between these ConnectID Terms and the Terms of Service in
        relation to your use of ConnectID, these ConnectID Terms prevail.
      </p>

      <h2>1. Definitions</h2>
      <p>
        In these ConnectID Terms: <strong>"AP+"</strong> means Australian
        Payments Plus Limited and its related bodies corporate.{" "}
        <strong>"ConnectID"</strong> means the digital identity verification and
        exchange service operated by AP+, including its scheme rules, brand and
        technology. <strong>"Representative"</strong> means FortifyID Pty Ltd
        (ACN 678 421 903), which is accredited by AP+ to make ConnectID
        available to its customers. <strong>"Beneficiary"</strong> means your
        organisation — the FortifyID customer that operates a workspace and
        relies on ConnectID to verify the identity of individuals.{" "}
        <strong>"Identity Provider"</strong> means a participant (such as a bank
        or other accredited provider) that holds and asserts verified identity
        attributes through ConnectID. <strong>"Subject"</strong> means an
        individual whose identity is verified through ConnectID at the
        Beneficiary's request. <strong>"ConnectID Data"</strong> means identity
        attributes, verification results and related information made available
        through ConnectID.
      </p>

      <h2>2. Acknowledgement of ConnectID and AP+</h2>
      <p>
        The Beneficiary acknowledges that: (a) ConnectID is owned and operated
        by AP+ and is provided to the Beneficiary through the Representative as
        an accredited ConnectID partner; (b) AP+ is not a party to these
        ConnectID Terms but is an intended third-party beneficiary of them and
        is entitled to enforce and rely on the Beneficiary's obligations,
        acknowledgements and limitations of liability in clauses 4 to 9; and
        (c) the Beneficiary's right to use ConnectID is conditional on its
        continued compliance with these ConnectID Terms and on the
        Representative's continued accreditation with AP+.
      </p>

      <h2>3. Grant and permitted use</h2>
      <p>
        Subject to these ConnectID Terms, the Representative grants the
        Beneficiary a non-exclusive, non-transferable, revocable right to use
        ConnectID, through the FortifyID platform, solely to verify the identity
        of Subjects for the Beneficiary's own legitimate business purposes in
        connection with the verification requests it issues. The Beneficiary
        must use ConnectID only as enabled and configured within the FortifyID
        platform and must not access or attempt to access ConnectID other than
        through the Representative.
      </p>

      <h2>4. Beneficiary obligations and restrictions</h2>
      <p>The Beneficiary must:</p>
      <ul>
        <li>
          comply with all ConnectID scheme rules, policies, brand guidelines and
          reasonable directions notified to it by the Representative or AP+ from
          time to time;
        </li>
        <li>
          only initiate identity verification where it has a genuine business
          need and a lawful basis to do so, and obtain each Subject's informed
          consent before verifying their identity through ConnectID;
        </li>
        <li>
          present any required consent, notice or collection statement to the
          Subject, and not mislead any Subject about the verification or its
          purpose;
        </li>
        <li>
          handle ConnectID Data in accordance with the Privacy Act 1988 (Cth)
          and the Australian Privacy Principles, use it only for the purpose for
          which it was collected, and not retain it for longer than is necessary
          for that purpose or as required by law;
        </li>
        <li>
          keep secure any credentials or configuration used to access ConnectID
          and promptly notify the Representative of any actual or suspected
          compromise, misuse or unauthorised access;
        </li>
        <li>
          not use ConnectID, ConnectID Data or the ConnectID name and marks for
          any purpose other than as permitted by these ConnectID Terms, and not
          represent that it is itself accredited by, or has a direct
          relationship with, AP+;
        </li>
        <li>
          not on-sell, sublicense, or provide ConnectID or ConnectID Data to any
          third party, and not use ConnectID to build a competing identity
          product or to profile Subjects beyond the verification purpose;
        </li>
        <li>
          not do anything that damages, or is reasonably likely to damage, the
          integrity, security or reputation of ConnectID, AP+, any Identity
          Provider, or the Representative.
        </li>
      </ul>

      <h2>5. Privacy and data</h2>
      <p>
        The Beneficiary is the entity responsible under the Australian Privacy
        Principles for its collection and handling of ConnectID Data about
        Subjects. The Representative processes ConnectID Data on the
        Beneficiary's behalf in accordance with the{" "}
        <Link href="/legal/dpa">Data Processing Agreement</Link>. The Beneficiary
        must not use ConnectID Data to make decisions about a Subject that have
        legal or similarly significant effects on the Subject in a manner that
        is unlawful, and must give Subjects a means to raise concerns about the
        verification.
      </p>

      <h2>6. Suspension</h2>
      <p>
        The Representative may suspend or restrict the Beneficiary's access to
        ConnectID immediately, with or without notice, where: (a) required to do
        so by AP+ or by law; (b) the Representative reasonably suspects misuse,
        fraud, a security incident, or a breach of these ConnectID Terms; or
        (c) the Representative's own accreditation with AP+ is suspended or
        terminated. The Representative will act reasonably and, where
        practicable, will notify the Beneficiary and work in good faith to
        restore access once the cause is resolved.
      </p>

      <h2>7. No warranty from AP+; limited service warranty</h2>
      <p>
        The Beneficiary acknowledges that ConnectID is provided on an "as
        available" basis and that, to the maximum extent permitted by law, AP+
        and the Identity Providers give no warranty in relation to ConnectID,
        the accuracy or completeness of any ConnectID Data, or the availability
        of the service. A verification result indicates the outcome of a
        verification at a point in time and is not a guarantee of a Subject's
        identity or of the absence of fraud. Nothing in these ConnectID Terms
        excludes, restricts or modifies any consumer guarantee or other right
        the Beneficiary has under the Australian Consumer Law that cannot
        lawfully be excluded.
      </p>

      <h2>8. Liability</h2>
      <p>
        To the maximum extent permitted by law, neither AP+ nor any Identity
        Provider is liable to the Beneficiary for any loss or damage arising out
        of or in connection with ConnectID or the Beneficiary's use of it, and
        the Beneficiary will not bring any claim against AP+ or an Identity
        Provider in connection with ConnectID. As between the Representative and
        the Beneficiary, the Representative's liability in connection with
        ConnectID is governed by, and subject to the limitations and exclusions
        in, the <Link href="/legal/terms">Terms of Service</Link>. The
        Beneficiary indemnifies the Representative and AP+ against loss arising
        from the Beneficiary's breach of clause 4, its misuse of ConnectID or
        ConnectID Data, or its failure to obtain a Subject's consent.
      </p>

      <h2>9. AP+ as third-party beneficiary</h2>
      <p>
        The Beneficiary agrees that AP+ may directly enforce clauses 2, 4, 5, 7
        and 8 against the Beneficiary as if AP+ were a party to these ConnectID
        Terms. These clauses survive termination of the Beneficiary's workspace
        or of these ConnectID Terms.
      </p>

      <h2>10. Term, changes and termination</h2>
      <p>
        These ConnectID Terms apply for as long as the Beneficiary has access to
        ConnectID through the FortifyID platform. AP+ may require the
        Representative to update these ConnectID Terms; where this happens, the
        Representative will publish the updated version and advise workspace
        administrators by email, and continued use of ConnectID after the update
        requires the Beneficiary to accept the new version. The Beneficiary's
        right to use ConnectID ends automatically if its workspace is suspended
        or terminated, or if the Representative ceases to be accredited by AP+.
      </p>

      <h2>11. Governing law</h2>
      <p>
        These ConnectID Terms are governed by the laws of New South Wales,
        Australia, and are subject to the jurisdiction provisions of the{" "}
        <Link href="/legal/terms">Terms of Service</Link>.
      </p>

      <h2>12. Contact</h2>
      <p>
        For questions about these ConnectID Terms, contact us via{" "}
        <Link href="/contact?topic=security">/contact</Link> or write to
        FortifyID Pty Ltd, Level 8, 11 York Street, Sydney NSW 2000, Australia.
      </p>
    </>
  );
}
